import { useQuery } from '@tanstack/react-query';
import { api, formatApiError } from '@/lib/api';

/**
 * Wrapper leggero su react-query per le LETTURE (GET) dell'app.
 *
 * @param {string|Array} key - queryKey stabile e univoca per la risorsa
 *   (es. ['public','hotels'] oppure ['customer','bookings']).
 * @param {Function|string} fnOrPath - o una funzione async che ritorna i dati
 *   (es. () => hotelService.publicStructures()), oppure un path GET stringa
 *   (es. '/customer/me/bookings') su cui viene fatto api.get automatico.
 * @param {object} [options] - opzioni react-query (enabled, staleTime, select,
 *   placeholderData, refetchInterval, ...).
 *
 * @returns l'oggetto standard di react-query + `errorMessage` (stringa GIA'
 *   formattata, anti React-#31) pronta da mostrare in UI.
 *
 * Vantaggi rispetto a useState+useEffect manuali: cache condivisa tra pagine,
 * de-duplica richieste identiche in volo, retry automatico, refetch on-demand,
 * stati loading/error standardizzati.
 */
export function useApiQuery(key, fnOrPath, options = {}) {
  const queryFn =
    typeof fnOrPath === 'function'
      ? fnOrPath
      : async ({ signal }) => (await api.get(fnOrPath, { signal })).data;

  const query = useQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn,
    retry: 1,
    ...options,
  });

  const errorMessage = query.isError
    ? formatApiError(query.error?.response?.data?.detail ?? query.error?.message)
    : '';

  return { ...query, errorMessage };
}
